import {
  ConditionalOptionValues,
  ConditionalStepField,
  CreditApplicationFields,
  DynamicInvitedSignaturesFields,
} from '@dill/dill-shared';
import React, { useState } from 'react';
import { COLORS } from '../../../../utils/colors';
import { PlusCircle, RadioButton, Trash } from 'phosphor-react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker, { parseDateValue } from './AppDatePicker';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';
import { UploadFileInputs } from './UploadFileInput';
import RadioInputs from './RadioInput';
import DocumentField from './DocumentField';
import InviteSignature from './InviteSignature';
import ConditionalOption from './ConditionalOption';
import { AppButton } from '../../../general/AppButton/AppButton';
import StateDropDown from './StateDropDown';
import OptionsDropDown from './OptionsDropDown';
import CheckboxList from './CheckboxList';
import AddressAutocompleteSelect from './AddressAutocompleteSelect';
import {
  buildUniqueId,
  groupRowsByPosition,
  isConditionalFieldVisible,
  OptionState,
  resolveFieldOptions,
  resolveDropdownFieldOptions,
  resolveSalesRepFieldLabel,
  SupplierRefOption,
} from './conditionalOptionUtils';
import { isHTML, toInlineHTML } from '../../../../utils/helpers';

// ── Public interface ─────────────────────────────────────────────────────────

/** Validate shape exposed by ConditionalOptionField (and DynamicStepForm) via ref */
export interface ConditionalOptionPreviewRef {
  validate: () => boolean;
}

export interface ConditionalOptionPreviewProps {
  creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
  name: string;
  isRequired?: boolean;
  isRow?: boolean;
  errorText?: string;

  /** Which radio option is currently active */
  selectedOption: string;
  /** Full state (fieldDef + groupedRows + values) for the active option */
  currentOptionState: OptionState | null;
  /** Validation errors keyed by field uniqueId */
  fieldErrors: { uniqueId: string; errorText: string }[];

  /** Original comma-separated option list — used to preserve display order */
  optionList?: string;

  /**
   * Hides the option radio-chip row — for callers with a single, implicit
   * option (e.g. TRADE_REFERENCE, which has no branching) where
   * `selectedOption` is always the fixed key and there's nothing to pick.
   */
  hideOptionSelector?: boolean;

  onSelectOption: (key: string) => void;
  onChangeValue: (args: { id: string; value: any; secondaryValue?: any }) => void;
  onAddGroup: () => void;
  onRemoveGroup: (args: { groupPosition: number }) => void;

  // Resolves the "supplier-branches"/"supplier-sales-reps" special-case sub-fields (see
  // resolveFieldOptions/resolveDropdownFieldOptions) — passed down from the caller's own
  // domain details, since a CONDITIONAL_SELECTED_FIELD's sub-fields have no other way to reach them.
  branches?: SupplierRefOption[];
  salesReps?: SupplierRefOption[];

  // Answers from the rest of the form, keyed by field id, for nested fields whose
  // conditionalVisibility points at a field outside this conditional.
  externalSourceValues?: Record<string, unknown>;
}

// ── FieldRenderer ────────────────────────────────────────────────────────────

interface FieldRendererProps {
  fields: CreditApplicationFields[];
  groupName: string | null | undefined;
  groupPosition: number;
  optionKey: string;
  stateValues: ConditionalOptionValues[];
  errors: { uniqueId: string; errorText: string }[];
  onChange: (args: { id: string; value: any; secondaryValue?: any }) => void;
  itemClassName?: string;
  innerClassName?: string;
  branches?: SupplierRefOption[];
  salesReps?: SupplierRefOption[];
  externalSourceValues?: Record<string, unknown>;
}

const formatEIN = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
};

const formatSSN = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 5) return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
};

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
};

const FieldRenderer = ({
  fields,
  groupName,
  groupPosition,
  optionKey,
  stateValues,
  errors,
  onChange,
  itemClassName = 'flex-shrink-0 flex-grow basis-[280px] sm:basis-[45%] lg:basis-[30%] max-w-full',
  innerClassName = 'py-2 w-full',
  branches,
  salesReps,
  externalSourceValues,
}: FieldRendererProps) => {
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [rawCurrencyText, setRawCurrencyText] = useState<Record<string, string>>({});

  // Drop fields hidden by their "Show this field only if…" rule.
  const visibleFields = fields.filter((field) =>
    isConditionalFieldVisible({
      field,
      optionValues: stateValues,
      externalSourceValues,
      groupedPosition: groupPosition,
    })
  );

  return (
    <>
      {visibleFields.map((field, idx) => {
        const uniqueId = buildUniqueId({
          optionKey,
          groupName,
          groupedPosition: groupPosition,
          fieldId: field.id || '',
        });
        let getValue = stateValues.find((v) => v.uniqueId === uniqueId)?.value;
        const getSecondaryValue = stateValues.find((v) => v.uniqueId === uniqueId)?.secondaryValue;
        const errorText =
          errors.find((e) => e.uniqueId === uniqueId)?.errorText || inlineErrors[uniqueId] || '';

        if ((field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') && getValue) {
          const parsed = Number(getValue);
          getValue = !isNaN(parsed) ? parsed : '';
        }

        let fieldComponent: React.ReactNode = null;

        switch (field.fromField) {
          case 'TEXT':
          case 'NUMBER':
          case 'CURRENCY':
          case 'TEXT_AREA':
            if (
              field.fromField === 'TEXT' &&
              (field.id || '').includes('-address') &&
              !(field.id || '').includes('email')
            ) {
              // A cloned group row's field ids carry a "-group-N" suffix (added by
              // duplicateWithNextGroupedPosition — see the 2nd/3rd Trade Reference, or any
              // repeated group past the first), so strip it before stripping "-address", and
              // strip it off each candidate's id too rather than requiring an exact match —
              // otherwise this only ever resolves siblings in the first (unsuffixed) group.
              const idPrefix = (field.id || '')
                .replace(/-group-\d+$/, '')
                .slice(0, -'-address'.length);
              const findSiblingByFieldId = (siblingId: string) =>
                stateValues.find(
                  (v) =>
                    v.id.replace(/-group-\d+$/, '') === siblingId &&
                    (v.groupName ?? '') === (groupName ?? '') &&
                    v.groupedPosition === groupPosition
                );
              const cityField = findSiblingByFieldId(`${idPrefix}-city`);
              const stateField = findSiblingByFieldId(`${idPrefix}-state`);
              const zipField = findSiblingByFieldId(`${idPrefix}-zip`);
              const hasAddressSiblings = !!cityField;

              fieldComponent = (
                <AddressAutocompleteSelect
                  label={field.name}
                  isRequired={field.isRequired}
                  value={typeof getValue === 'string' ? getValue : ''}
                  errorText={errorText}
                  dataPrivate={!!field?.isHashed}
                  onTextChange={(text) => onChange({ id: uniqueId, value: text })}
                  onSelectSuggestion={(suggestion) => {
                    const streetLine = [suggestion.streetLine, suggestion.secondary]
                      .filter(Boolean)
                      .join(' ');
                    if (hasAddressSiblings) {
                      onChange({ id: uniqueId, value: streetLine });
                      if (cityField) onChange({ id: cityField.uniqueId, value: suggestion.city });
                      if (stateField)
                        onChange({ id: stateField.uniqueId, value: suggestion.state });
                      if (zipField) onChange({ id: zipField.uniqueId, value: suggestion.zipcode });
                    }
                  }}
                />
              );
              break;
            }
            fieldComponent = (
              <>
                <AppInputField
                  label={field.name}
                  inputType={
                    field.fromField === 'TEXT_AREA'
                      ? 'textarea'
                      : field.fromField === 'NUMBER'
                      ? 'number'
                      : 'text'
                  }
                  value={
                    field.fromField === 'CURRENCY'
                      ? rawCurrencyText[uniqueId] !== undefined
                        ? rawCurrencyText[uniqueId]
                        : typeof getValue === 'number'
                        ? getValue.toLocaleString('en-US', {
                            minimumFractionDigits: (field as any).currencyDecimalPlaces ?? 2,
                            maximumFractionDigits: (field as any).currencyDecimalPlaces ?? 2,
                          })
                        : ''
                      : field.fromField === 'NUMBER' && typeof getValue === 'number'
                      ? getValue
                      : (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                        typeof getValue === 'string'
                      ? getValue
                      : ''
                  }
                  isRequired={field.isRequired}
                  errorText={errorText}
                  suffix={field.suffix}
                  prefix={field.prefix}
                  placeholder={
                    field.validationType === 'EIN'
                      ? 'XX-XXXXXXX'
                      : field.validationType === 'SSN'
                      ? 'XXX-XX-XXXX'
                      : field.validationType === 'PHONE'
                      ? 'XXX-XXX-XXXX'
                      : undefined
                  }
                  maxLength={
                    field.validationType === 'EIN'
                      ? 10
                      : field.validationType === 'SSN'
                      ? 11
                      : field.validationType === 'PHONE'
                      ? 12
                      : field.maxLength
                  }
                  onBlur={() => {
                    if (field.fromField === 'CURRENCY') {
                      setRawCurrencyText((prev) => {
                        const next = { ...prev };
                        delete next[uniqueId];
                        return next;
                      });
                      return;
                    }
                    if (
                      (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                      typeof getValue === 'string'
                    ) {
                      let val = getValue.trim();
                      if (field.isFieldEmail) {
                        if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
                          val = val
                            .split(/[,;]/)
                            .map((e: string) => e.trim().toLowerCase())
                            .filter(Boolean)
                            .join(', ');
                        } else {
                          val = val.toLowerCase();
                        }
                      }
                      onChange({ id: uniqueId, value: val });
                      if (val && field.validationType === 'EIN') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{2}-\d{7}$/.test(val)
                            ? ''
                            : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                        }));
                      } else if (val && field.validationType === 'SSN') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{2}-\d{4}$/.test(val)
                            ? ''
                            : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                        }));
                      } else if (val && field.validationType === 'PHONE') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{3}-\d{4}$/.test(val)
                            ? ''
                            : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                        }));
                      }
                    }
                  }}
                  onTextChange={(text) => {
                    if (field.fromField === 'CURRENCY') {
                      const decPlaces: number = (field as any).currencyDecimalPlaces ?? 2;
                      const stripped = text.replace(/[$,]/g, '');
                      if (!/^\d*\.?\d*$/.test(stripped)) return;
                      if (decPlaces === 0 && stripped.includes('.')) return;
                      const decMatch = stripped.match(/\.(\d+)/);
                      if (decMatch && decMatch[1].length > decPlaces) return;
                      const intPart = stripped.split('.')[0];
                      const hasDecimal = stripped.includes('.');
                      const decPart = stripped.split('.')[1] ?? '';
                      const formattedInt = intPart
                        ? parseInt(intPart, 10).toLocaleString('en-US')
                        : '';
                      const displayValue = formattedInt + (hasDecimal ? '.' + decPart : '');
                      setRawCurrencyText((prev) => ({ ...prev, [uniqueId]: displayValue }));
                      const numericValue = parseFloat(stripped);
                      onChange({
                        id: uniqueId,
                        value: stripped === '' || isNaN(numericValue) ? null : numericValue,
                      });
                      return;
                    }
                    let formatted = text;
                    if (field.fromField === 'TEXT') {
                      if (field.validationType === 'EIN') formatted = formatEIN(text);
                      else if (field.validationType === 'SSN') formatted = formatSSN(text);
                      else if (field.validationType === 'PHONE') formatted = formatPhone(text);
                    }
                    if (field.fromField === 'TEXT') {
                      if (field.validationType === 'EIN' && formatted.length === 10) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{2}-\d{7}$/.test(formatted)
                            ? ''
                            : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                        }));
                      } else if (field.validationType === 'SSN' && formatted.length === 11) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{2}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                        }));
                      } else if (field.validationType === 'PHONE' && formatted.length === 12) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{3}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                        }));
                      } else if (
                        (field.validationType === 'EIN' ||
                          field.validationType === 'SSN' ||
                          field.validationType === 'PHONE') &&
                        inlineErrors[uniqueId]
                      ) {
                        setInlineErrors((prev) => ({ ...prev, [uniqueId]: '' }));
                      }
                    }
                    onChange({
                      id: uniqueId,
                      value:
                        field.fromField === 'NUMBER' ? (text ? parseFloat(text) : null) : formatted,
                    });
                  }}
                  labelTextClassName="whitespace-pre-wrap"
                  dataPrivate={!!field?.isHashed}
                />
                {field.validationType === 'EIN' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XX-XXXXXXX (e.g. 12-3456789)
                  </p>
                )}
                {field.validationType === 'SSN' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XXX-XX-XXXX (e.g. 123-45-6789)
                  </p>
                )}
                {field.validationType === 'PHONE' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XXX-XXX-XXXX (e.g. 555-123-4567)
                  </p>
                )}
                {(field.emailValidationType ?? 'SINGLE') === 'MULTIPLE' && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Separate multiple emails with a comma or semicolon
                  </p>
                )}
              </>
            );
            break;

          case 'DATE':
            fieldComponent = (
              <AppDatePicker
                label={field.name}
                selectedDate={parseDateValue(getValue)}
                errorText={errorText}
                isRequired={field.isRequired}
                onDateSelected={(date) => onChange({ id: uniqueId, value: date })}
              />
            );
            break;

          case 'CHECK_BOX':
            fieldComponent = (
              <div>
                <div
                  className={`my-1 flex items-center w-full ${
                    field.alignment === 'RIGHT'
                      ? 'justify-end'
                      : field.alignment === 'CENTER'
                      ? 'justify-center'
                      : 'justify-start'
                  }`}>
                  <DillCheckbox
                    isChecked={typeof getValue === 'boolean' ? getValue : false}
                    onChange={(checked: boolean) => onChange({ id: uniqueId, value: checked })}
                  />
                  <div className="ml-2 text-sm">{field.name}</div>
                </div>
                {errorText && (
                  <p
                    className={`text-2xs ml-2 text-red-500 w-full ${
                      field.alignment === 'RIGHT'
                        ? 'text-end'
                        : field.alignment === 'CENTER'
                        ? 'text-center'
                        : 'text-start'
                    }`}>
                    {errorText}
                  </p>
                )}
              </div>
            );
            break;

          case 'FILE':
            fieldComponent = (
              <UploadFileInputs
                isRequired={field.isRequired}
                errorText={errorText}
                label={field.name}
                labelNode={
                  <span className="flex items-center gap-1 text-xs GREY_800-CLR">
                    <span>
                      {field.isRequired && <span className="ERROR_500-CLR mr-0.5">*</span>}
                      {field.name}
                    </span>
                    {field.sampleFile?.url && (
                      <a
                        href={field.sampleFile.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-normal text-blue-500 underline">
                        (View sample)
                      </a>
                    )}
                  </span>
                }
                selectedFile={getValue instanceof File ? getValue : null}
                selectedFileName={typeof getValue === 'object' ? getValue?.name : null}
                onSelectFile={(data) => onChange({ id: uniqueId, value: data.file })}
                onRemoveFile={() => onChange({ id: uniqueId, value: null })}
              />
            );
            break;

          case 'OPTION_FIELD':
            fieldComponent = (
              <div className="flex items-center">
                <RadioInputs
                  label={resolveSalesRepFieldLabel(field, getValue, salesReps)}
                  onChange={(option) => onChange({ id: uniqueId, value: option.value })}
                  id={field.name}
                  options={resolveFieldOptions(field, branches, salesReps)}
                  optionDescriptions={
                    field?.optionDescriptions
                      ? field.optionDescriptions.split(';;').map((o) => o.trim())
                      : undefined
                  }
                  isRequired={field.isRequired}
                  selectedOption={typeof getValue === 'string' ? getValue : ''}
                  className={field.isRow ? 'gap-2 flex items-center' : ''}
                  errorText={errorText}
                  labelTextClassName="whitespace-pre-wrap"
                />
              </div>
            );
            break;

          case 'DOCUMENT_FIELD':
            fieldComponent = (
              <div className="flex items-center w-full">
                <DocumentField
                  text={field.documentText || ''}
                  className={`w-full ${field.fontWeight} text-[${field.fontSize ?? '12'}]px ${
                    field.alignment === 'RIGHT'
                      ? 'text-right'
                      : field.alignment === 'CENTER'
                      ? 'text-center'
                      : 'text-left'
                  }`}
                />
              </div>
            );
            break;

          case 'INVITE_USER':
            fieldComponent = (
              <InviteSignature
                data={
                  field?.whoNeedsToSign
                    ? field.whoNeedsToSign.split(',').map((o) => ({
                        title: o.trim(),
                        email: '',
                        step: 0,
                        signatureGroupName: field.name,
                        placeHolder: o.trim(),
                        type: field?.type ?? 'AUTHORIZED_AGENT',
                        minimumCreditAmount: field?.minimumCreditAmount,
                        fields: field.invitedSignaturesFields || [],
                        minimumNumberOfSignatures: field?.minimumNumberOfSignatures,
                        isRequired: field?.isRequired,
                        id: field?.id,
                      }))
                    : ([] as {
                        title: string;
                        email: string;
                        step: number;
                        signatureGroupName: string;
                        placeHolder: string;
                        type:
                          | 'AUTHORIZED_AGENT'
                          | 'GUARANTOR'
                          | 'OTHER'
                          | 'PERSONAL'
                          | 'CORPORATE_GUARANTOR';
                        minimumCreditAmount?: number;
                        fields: DynamicInvitedSignaturesFields[];
                        minimumNumberOfSignatures?: number;
                        isRequired?: boolean;
                        id?: string;
                      }[])
                }
                onChange={() => {}}
                value={[]}
                amountOfCredit={2000}
              />
            );
            break;

          case 'CONDITIONAL_OPTION':
            fieldComponent = (
              <ConditionalOption
                optionType={field?.optionType ?? ''}
                id={field.id}
                onChange={(data) =>
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    secondaryValue: data.secondaryValue,
                  })
                }
                isRequired={field.isRequired}
                label={field.name}
                selectedValue={{ value: getValue, secondaryValue: getSecondaryValue }}
                secondaryText={field.secondaryText}
                errorText={errorText}
                currentValue={
                  typeof getValue === 'object'
                    ? getValue?.name
                    : typeof getValue === 'string'
                    ? getValue
                    : null
                }
              />
            );
            break;

          case 'STATE_DROPDOWN':
            fieldComponent = (
              <StateDropDown
                isRequired={field.isRequired}
                label={field.name}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errorText}
                onTextChange={(text) => onChange({ id: uniqueId, value: text })}
              />
            );
            break;

          case 'CHECKBOX_LIST':
            fieldComponent = (
              <CheckboxList
                isRequired={field.isRequired}
                label={resolveSalesRepFieldLabel(field, getValue, salesReps)}
                options={resolveFieldOptions(field, branches, salesReps)}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errorText}
                onTextChange={(text) => onChange({ id: uniqueId, value: text })}
              />
            );
            break;

          case 'DROPDOWN':
            fieldComponent = (
              <OptionsDropDown
                isRequired={field.isRequired}
                label={resolveSalesRepFieldLabel(field, getValue, salesReps)}
                options={resolveDropdownFieldOptions(field, branches, salesReps)}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errorText}
                onTextChange={(text) => onChange({ id: uniqueId, value: text })}
              />
            );
            break;

          default:
            fieldComponent = (
              <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
            );
        }

        return (
          <div key={field.id || idx} className={itemClassName}>
            <div className={innerClassName}>{fieldComponent}</div>
          </div>
        );
      })}
    </>
  );
};

// ── Main component (pure renderer — no internal state for values) ─────────────

const ConditionalOptionPreview = ({
  creditApplicationOptionData,
  name,
  isRequired,
  isRow,
  errorText,
  optionList,
  hideOptionSelector,
  selectedOption,
  currentOptionState,
  fieldErrors,
  onSelectOption,
  onChangeValue,
  onAddGroup,
  onRemoveGroup,
  branches,
  salesReps,
  externalSourceValues,
}: ConditionalOptionPreviewProps) => {
  const rawKeys = creditApplicationOptionData ? Object.keys(creditApplicationOptionData) : [];
  const options = optionList
    ? optionList
        .split(',')
        .map((o) => o.trim())
        .filter((o) => rawKeys.includes(o))
    : rawKeys;

  const groupedFieldsByPosition =
    currentOptionState?.fieldDef.stepType === 'GROUPED'
      ? groupRowsByPosition(currentOptionState.groupedRows)
      : null;

  const minGroups = currentOptionState?.fieldDef.minimunNumberOfGroups ?? 1;
  const getColumnsCount = (type: string) =>
    type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

  return (
    <div>
      {!hideOptionSelector && (
        <>
          <div className="font-medium text-xs mb-1 text-left">
            {isRequired && <span className="text-red-500">*</span>}
            {isHTML(name) ? (
              <span dangerouslySetInnerHTML={{ __html: toInlineHTML(name) }} />
            ) : (
              name
            )}
          </div>

          <div className={isRow ? 'flex flex-col' : 'flex flex-wrap flex-row gap-2 md:gap-4'}>
            {options.map((key) => (
              <div
                key={key}
                className="flex items-center mb-1 cursor-pointer gap-1"
                onClick={() => onSelectOption(key)}>
                <RadioButton
                  weight={selectedOption === key ? 'fill' : 'thin'}
                  size={20}
                  color={selectedOption === key ? COLORS.PRIMARY_500 : COLORS.GREY_500}
                />
                <div className="text-xs">{key}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {errorText && <div className="text-red-500 text-[9px] mb-1">{errorText}</div>}

      {selectedOption && currentOptionState && (
        <div className="mt-2">
          {(() => {
            const hasGrouped =
              groupedFieldsByPosition != null && Object.keys(groupedFieldsByPosition).length > 0;
            const hasUngrouped = (currentOptionState.fieldDef.unGroupedFields ?? []).length > 0;
            const order: Array<'GROUPED' | 'NOT_GROUPED'> =
              currentOptionState.fieldDef.sectionOrder ??
              (hasGrouped ? ['GROUPED'] : ['NOT_GROUPED']);

            const groupedBlock = hasGrouped ? (
              <div className="PRIMARY_100-BG rounded-md">
                <p className="text-sm font-bold ml-2 pt-2">
                  {currentOptionState.fieldDef.groupName}
                </p>

                {Object.values(groupedFieldsByPosition ?? {}).map((rowGroup, j, all) => (
                  <div
                    key={`group-${currentOptionState.fieldDef.groupName}-${
                      rowGroup[0]?.groupedPosition ?? j
                    }`}
                    className="w-full border-b GREY_300-BORDER p-2">
                    {currentOptionState.fieldDef.isFieldArray &&
                      all.length > minGroups &&
                      j === all.length - 1 && (
                        <div
                          className="w-full flex mb-2 justify-end cursor-pointer text-red-500 items-center px-2"
                          onClick={() =>
                            onRemoveGroup({
                              groupPosition: rowGroup[0]?.groupedPosition ?? 0,
                            })
                          }>
                          <div className="text-sm mr-1">Remove</div>
                          <Trash color={COLORS.ERROR_400} size={16} />
                        </div>
                      )}

                    {rowGroup.map((row, findex) => {
                      const columns = getColumnsCount(row.columnType);
                      const fieldsArray = Array(columns)
                        .fill(null)
                        .map(
                          (_, i) =>
                            row.fields[i] ?? {
                              name: '',
                              id: '',
                              fromField: 'TEXT',
                              isRequired: false,
                              prefix: '',
                              suffix: '',
                            }
                        );
                      return (
                        <div key={`${row.id}-${findex}`} className="w-full">
                          <div className="flex flex-wrap gap-4 md:gap-2">
                            <FieldRenderer
                              fields={fieldsArray}
                              groupName={currentOptionState.fieldDef.groupName}
                              groupPosition={row.groupedPosition ?? 0}
                              optionKey={selectedOption}
                              stateValues={currentOptionState.values}
                              errors={fieldErrors}
                              onChange={onChangeValue}
                              branches={branches}
                              salesReps={salesReps}
                              externalSourceValues={externalSourceValues}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                {currentOptionState.fieldDef.isFieldArray && (
                  <div className="w-full flex mb-2 justify-end py-2 px-4">
                    <AppButton
                      text={
                        currentOptionState.fieldDef.moreButtonName ||
                        `Add ${currentOptionState.fieldDef.groupName}`
                      }
                      onClick={onAddGroup}
                      icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                      buttonStyles={{ height: '30px', borderRadius: '5px' }}
                    />
                  </div>
                )}
              </div>
            ) : null;

            const renderUngroupedBlock = (
              fields: CreditApplicationFields[],
              columnType: string
            ) => {
              const isMultiColumn = columnType !== 'ONE_COLUMN';
              const renderFields = (runFields: CreditApplicationFields[]) => (
                <FieldRenderer
                  fields={runFields}
                  groupName={null}
                  groupPosition={0}
                  optionKey={selectedOption}
                  stateValues={currentOptionState.values}
                  errors={fieldErrors}
                  onChange={onChangeValue}
                  itemClassName={isMultiColumn ? 'flex-1' : 'w-full'}
                  innerClassName="w-full pb-2"
                  branches={branches}
                  salesReps={salesReps}
                  externalSourceValues={externalSourceValues}
                />
              );

              // Same border groups as DynamicStepForm: a field with borderId boxes itself
              // through the field whose id matches, inclusive.
              const runs: { fields: CreditApplicationFields[]; bordered: boolean }[] = [];
              let fi = 0;
              while (fi < fields.length) {
                const borderId = fields[fi].borderId;
                const endIdx = borderId ? fields.findIndex((f) => f.id === borderId) : -1;
                if (endIdx >= fi) {
                  runs.push({ fields: fields.slice(fi, endIdx + 1), bordered: true });
                  fi = endIdx + 1;
                  continue;
                }
                const last = runs[runs.length - 1];
                if (last && !last.bordered) last.fields.push(fields[fi]);
                else runs.push({ fields: [fields[fi]], bordered: false });
                fi += 1;
              }

              const layoutClass = isMultiColumn ? 'flex flex-row gap-2' : 'flex flex-col';
              if (!runs.some((r) => r.bordered)) {
                return <div className={layoutClass}>{renderFields(fields)}</div>;
              }
              return (
                <div className="flex flex-col">
                  {runs.map((run, ri) =>
                    run.bordered ? (
                      <div
                        key={`bordered-conditional-run-${ri}`}
                        className={`border border-gray-200 rounded-lg p-2 mb-2 ${layoutClass}`}>
                        {renderFields(run.fields)}
                      </div>
                    ) : (
                      <div key={`conditional-run-${ri}`} className={layoutClass}>
                        {renderFields(run.fields)}
                      </div>
                    )
                  )}
                </div>
              );
            };

            // Split the flat ungrouped list back into the author's sections when the
            // layout was saved; older templates render everything as one section.
            const unGroupedFields = currentOptionState.fieldDef.unGroupedFields ?? [];
            const ungroupedLayout = currentOptionState.fieldDef.ungroupedSectionLayout ?? [];
            let ungroupedLayoutIdx = 0;
            let ungroupedFieldOffset = 0;

            const gapMap: Record<string, string> = {
              none: 'mt-0',
              sm: 'mt-2',
              md: 'mt-4',
              lg: 'mt-6',
              xl: 'mt-8',
            };
            const gapClass = gapMap[currentOptionState.fieldDef.sectionGap ?? 'sm'] ?? 'mt-2';

            // Each rendered section, with the slice of unGroupedFields it covers (ungrouped only)
            // so border groups can span sections.
            const sectionNodes: {
              key: string;
              node: React.ReactNode;
              start: number;
              end: number;
            }[] = [];
            const seen = new Set<string>();
            order.forEach((type, i) => {
              if (type === 'NOT_GROUPED' && ungroupedLayoutIdx < ungroupedLayout.length) {
                const layout = ungroupedLayout[ungroupedLayoutIdx];
                ungroupedLayoutIdx += 1;
                const sectionStart = ungroupedFieldOffset;
                ungroupedFieldOffset += layout.fieldCount;
                const sectionFields = unGroupedFields.slice(sectionStart, ungroupedFieldOffset);
                if (sectionFields.length === 0) return;
                sectionNodes.push({
                  key: `${type}-${i}`,
                  node: renderUngroupedBlock(sectionFields, layout.columnType),
                  start: sectionStart,
                  end: ungroupedFieldOffset,
                });
                return;
              }
              if (seen.has(type)) return;
              seen.add(type);
              if (type === 'GROUPED') {
                if (groupedBlock)
                  sectionNodes.push({
                    key: `${type}-${i}`,
                    node: groupedBlock,
                    start: -1,
                    end: -1,
                  });
              } else if (hasUngrouped && ungroupedLayout.length === 0) {
                sectionNodes.push({
                  key: `${type}-${i}`,
                  node: renderUngroupedBlock(
                    unGroupedFields,
                    currentOptionState.fieldDef.columnType ?? 'ONE_COLUMN'
                  ),
                  start: 0,
                  end: unGroupedFields.length,
                });
              }
            });

            // A borderId pointing past its own section boxes every section through the one
            // holding the target field (in-section groups are boxed by renderUngroupedBlock).
            const rendered: React.ReactNode[] = [];
            let ni = 0;
            while (ni < sectionNodes.length) {
              const current = sectionNodes[ni];
              let clusterEnd = ni;
              if (current.start >= 0) {
                unGroupedFields.slice(current.start, current.end).forEach((f) => {
                  if (!f.borderId) return;
                  const targetIdx = unGroupedFields.findIndex((t) => t.id === f.borderId);
                  if (targetIdx < current.end) return;
                  const targetNode = sectionNodes.findIndex(
                    (sn) => sn.start >= 0 && targetIdx >= sn.start && targetIdx < sn.end
                  );
                  if (targetNode > clusterEnd) clusterEnd = targetNode;
                });
              }
              const outerClass = rendered.length > 0 ? gapClass : '';
              if (clusterEnd === ni) {
                rendered.push(
                  <div key={current.key} className={outerClass}>
                    {current.node}
                  </div>
                );
              } else {
                rendered.push(
                  <div
                    key={`bordered-sections-${current.key}`}
                    className={`border border-gray-200 rounded-lg p-2 ${outerClass}`}>
                    {sectionNodes.slice(ni, clusterEnd + 1).map((sn, si) => (
                      <div key={sn.key} className={si > 0 ? gapClass : ''}>
                        {sn.node}
                      </div>
                    ))}
                  </div>
                );
              }
              ni = clusterEnd + 1;
            }
            return rendered;
          })()}
        </div>
      )}
    </div>
  );
};

export default ConditionalOptionPreview;
